/**
 * Authentication service
 * Handles all authentication-related API calls with real backend
 */

import type { LoginCredentials, LoginResponse, User, UserRole } from '@/types/auth';
import type { ISODateString } from '@/types/api';
import { HttpError, httpClient } from '@/utils/httpClient';
import type { LoginRequest, LoginResponse as BackendLoginResponse } from '@/types/api-schemas';

// Token keys (aligned with httpClient.ts expectations)
const TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';

/**
 * Store authentication tokens in localStorage
 */
function storeTokens(accessToken: string, refreshToken: string): void {
    localStorage.setItem(TOKEN_KEY, accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
}

/**
 * Clear all stored authentication data
 */
function clearTokens(): void {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem('user');
}

/**
 * Get stored access token
 */
export function getAccessToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
}

/**
 * Get stored refresh token
 */
export function getRefreshToken(): string | null {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
}

/**
 * Check if user has stored tokens
 */
export function hasStoredTokens(): boolean {
    return getAccessToken() !== null && getRefreshToken() !== null;
}

/**
 * Map backend login response to frontend User type
 */
function mapBackendUser(response: BackendLoginResponse): User {
    const normalizeRole = (role: string): UserRole => {
        const normalized = role
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .trim()
            .toLowerCase();

        if (normalized === 'cliente' || normalized === 'client') return 'client';
        if (normalized === 'tecnico' || normalized === 'technician') return 'technician';
        if (normalized === 'manager' || normalized === 'gerente') return 'manager';
        if (normalized === 'admin' || normalized === 'administrador') return 'admin';
        if (normalized === 'user' || normalized === 'usuario') return 'user';

        return 'user';
    };

    return {
        id: String(response.id),
        email: response.username,
        firstName: response.first_name,
        lastName: response.last_name,
        role: normalizeRole(response.role),
        lastLoginAt: new Date().toISOString() as ISODateString,
        createdAt: new Date().toISOString() as ISODateString,
    };
}

/**
 * Authentication service
 */
export const authService = {
    /**
     * Login with credentials using backend API
     */
    async login(credentials: LoginCredentials): Promise<LoginResponse> {
        try {
            const loginRequest: LoginRequest = {
                username: credentials.email,
                password: credentials.password,
            };

            const response = await httpClient.post<BackendLoginResponse>(
                '/auth/login/',
                loginRequest
            );

            const accessToken = response.access_token ?? response.access;
            const refreshToken = response.refresh_token ?? response.refresh;

            if (!accessToken || !refreshToken) {
                throw new HttpError(500, 'Invalid login token response');
            }

            storeTokens(accessToken, refreshToken);

            const userData = mapBackendUser(response);
            localStorage.setItem('user', JSON.stringify(userData));

            return {
                user: userData,
                accessToken,
                refreshToken,
                expiresAt: new Date(Date.now() + 3600000).toISOString() as ISODateString,
            };
        } catch (error) {
            if (error instanceof HttpError) {
                if (error.statusCode === 401) {
                    throw new HttpError(401, 'Invalid credentials', {
                        code: 'INVALID_CREDENTIALS',
                        message: 'Credenciales inválidas',
                        timestamp: new Date().toISOString(),
                    });
                }
                if (error.statusCode === 400) {
                    throw new HttpError(400, 'Bad request', {
                        code: 'BAD_REQUEST',
                        message: error.message,
                        timestamp: new Date().toISOString(),
                    });
                }
            }
            throw error;
        }
    },

    /**
     * Logout current user — clears local tokens
     */
    async logout(): Promise<void> {
        clearTokens();
    },

    /**
     * Get current authenticated user from localStorage
     */
    async getCurrentUser(): Promise<User> {
        const token = getAccessToken();

        if (!token) {
            throw new HttpError(401, 'No authentication token found', {
                code: 'UNAUTHORIZED',
                message: 'No authentication token found',
                timestamp: new Date().toISOString(),
            });
        }

        const userStr = localStorage.getItem('user');

        if (!userStr) {
            throw new HttpError(401, 'User not found', {
                code: 'UNAUTHORIZED',
                message: 'User not found',
                timestamp: new Date().toISOString(),
            });
        }

        return JSON.parse(userStr) as User;
    },

    /**
     * Refresh access token using refresh token
     */
    async refreshToken(): Promise<LoginResponse> {
        const refreshToken = getRefreshToken();

        if (!refreshToken) {
            throw new HttpError(401, 'No refresh token found', {
                code: 'UNAUTHORIZED',
                message: 'No refresh token found',
                timestamp: new Date().toISOString(),
            });
        }

        try {
            const response = await httpClient.post<{
                access?: string;
                access_token?: string;
                refresh?: string;
                refresh_token?: string;
            }>(
                '/auth/refresh/',
                { refresh: refreshToken }
            );

            const nextAccessToken = response.access ?? response.access_token;
            const nextRefreshToken = response.refresh ?? response.refresh_token ?? refreshToken;

            if (!nextAccessToken) {
                throw new HttpError(401, 'Invalid refresh token response');
            }

            localStorage.setItem(TOKEN_KEY, nextAccessToken);
            localStorage.setItem(REFRESH_TOKEN_KEY, nextRefreshToken);

            const userStr = localStorage.getItem('user');
            if (!userStr) {
                throw new HttpError(401, 'User not found');
            }

            const user = JSON.parse(userStr) as User;

            return {
                user,
                accessToken: nextAccessToken,
                refreshToken: nextRefreshToken,
                expiresAt: new Date(Date.now() + 3600000).toISOString() as ISODateString,
            };
        } catch (error) {
            if (error instanceof HttpError && error.statusCode === 401) {
                clearTokens();
            }
            throw error;
        }
    },

    /**
     * Clear all authentication data
     */
    clearAuth(): void {
        clearTokens();
    },
} as const;

export default authService;
